export type PopoverPosition =
  | 'bottom-start'
  | 'bottom-center'
  | 'bottom-end'
  | 'top-start'
  | 'top-center'
  | 'top-end'
  | 'left-start'
  | 'left-center'
  | 'left-end'
  | 'right-start'
  | 'right-center'
  | 'right-end';

export interface PopoverRect {
  top: number;
  right: number;
  bottom: number;
  left: number;
  width: number;
  height: number;
}

export interface PopoverCoordinates {
  top: number;
  left: number;
  maxWidth: number;
  maxHeight: number;
}

function alignHorizontal(
  trigger: PopoverRect,
  panelWidth: number,
  position: PopoverPosition
): number {
  if (position.endsWith('end')) return trigger.right - panelWidth;
  if (position.endsWith('center')) {
    return trigger.left + trigger.width / 2 - panelWidth / 2;
  }
  return trigger.left;
}

function alignVertical(
  trigger: PopoverRect,
  panelHeight: number,
  position: PopoverPosition
): number {
  if (position.endsWith('end')) return trigger.bottom - panelHeight;
  if (position.endsWith('center')) {
    return trigger.top + trigger.height / 2 - panelHeight / 2;
  }
  return trigger.top;
}

function fitsHorizontally(
  left: number,
  panelWidth: number,
  viewportWidth: number,
  padding: number
): boolean {
  return left >= padding && left + panelWidth <= viewportWidth - padding;
}

export function calculatePopoverCoordinates(params: {
  trigger: PopoverRect;
  panelWidth: number;
  panelHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  position: PopoverPosition;
  gap?: number;
  padding?: number;
}): PopoverCoordinates {
  const gap = params.gap ?? 8;
  const padding = params.padding ?? 12;
  const maxWidth = Math.max(0, params.viewportWidth - padding * 2);
  const maxHeight = Math.max(0, params.viewportHeight - padding * 2);
  const panelWidth = Math.min(params.panelWidth, maxWidth);
  const panelHeight = Math.min(params.panelHeight, maxHeight);
  const { trigger, position } = params;

  let left: number;
  let top: number;

  if (position.startsWith('bottom') || position.startsWith('top')) {
    const preferredLeft = alignHorizontal(trigger, panelWidth, position);
    const alternateLeft = position.endsWith('end')
      ? trigger.left
      : position.endsWith('start')
        ? trigger.right - panelWidth
        : preferredLeft;
    left = fitsHorizontally(preferredLeft, panelWidth, params.viewportWidth, padding)
      ? preferredLeft
      : fitsHorizontally(alternateLeft, panelWidth, params.viewportWidth, padding)
        ? alternateLeft
        : preferredLeft;
    const below = trigger.bottom + gap;
    const above = trigger.top - gap - panelHeight;
    if (position.startsWith('bottom')) {
      const belowFits = below + panelHeight <= params.viewportHeight - padding;
      const aboveFits = above >= padding;
      if (belowFits) {
        top = below;
      } else if (aboveFits) {
        top = above;
      } else {
        top = above;
      }
    } else {
      const aboveFits = above >= padding;
      const belowFits = below + panelHeight <= params.viewportHeight - padding;
      if (aboveFits) {
        top = above;
      } else if (belowFits) {
        top = below;
      } else {
        top = above;
      }
    }
  } else {
    top = alignVertical(trigger, panelHeight, position);
    const right = trigger.right + gap;
    const leftSide = trigger.left - gap - panelWidth;
    if (position.startsWith('right')) {
      const rightFits = right + panelWidth <= params.viewportWidth - padding;
      const leftFits = leftSide >= padding;
      if (rightFits) {
        left = right;
      } else if (leftFits) {
        left = leftSide;
      } else {
        left = right;
      }
    } else {
      const leftFits = leftSide >= padding;
      const rightFits = right + panelWidth <= params.viewportWidth - padding;
      if (leftFits) {
        left = leftSide;
      } else if (rightFits) {
        left = right;
      } else {
        left = leftSide;
      }
    }
  }

  return {
    top: Math.min(Math.max(top, padding), Math.max(padding, params.viewportHeight - padding - panelHeight)),
    left: Math.min(Math.max(left, padding), Math.max(padding, params.viewportWidth - padding - panelWidth)),
    maxWidth,
    maxHeight,
  };
}
